import { Injectable } from '@angular/core';
import { AuthStatePersistenceService, EventService, RoutingService } from '@spartacus/core';
import { Ga4GgScrollEvent } from '../../events/analytics/ga4/general/ga4-gg-scroll.event';
import { Ga4GgReaderTypeEvent } from '../../events/analytics/ga4/general/ga4-gg-reader-type.event';

type Target = Document | Element;

@Injectable({
  providedIn: 'root',
})
export class ScrollPercentageService {
  private scrolled10PercentEventDispatched: boolean = false;
  private scrolled25PercentEventDispatched: boolean = false;
  private scrolled50PercentEventDispatched: boolean = false;
  private scrolled75PercentEventDispatched: boolean = false;
  private scrolled90PercentEventDispatched: boolean = false;
  private navigationToPageDate: number = Date.now();
  private pageType!: string;
  private pageOnTop: boolean = false;

  constructor(
    private eventService: EventService,
    private authStatePersistenceService: AuthStatePersistenceService,
    private routingService: RoutingService
  ) {
    this.routingService.getRouterState().subscribe((routerState) => {
      if (routerState.state.semanticRoute === 'home') {
        this.pageType = 'Homepage';
      } else if (routerState.state.semanticRoute) {
        this.pageType = 'Category listing';
      } else if (routerState.state.semanticRoute?.includes('checkout')) {
        this.pageType = 'Checkout';
      } else if (routerState.state.semanticRoute === 'search') {
        this.pageType = 'Search result';
      } else {
        this.pageType = 'Content';
      }
    });
  }

  private getScroll(node: Target, currentScroll: number): number {
    const maxScroll = this.getMaxScroll(node);

    let percent = currentScroll / Math.max(maxScroll, 1);
    percent = Math.max(percent, 0);
    percent = Math.min(percent, 1);

    return percent * 100;
  }

  private getMaxScroll(node: Target): number {
    if (node instanceof Document) {
      const scrollHeight = Math.max(
        node.body.scrollHeight,
        node.body.offsetHeight,
        node.body.clientHeight,
        node.documentElement.scrollHeight,
        node.documentElement.offsetHeight,
        node.documentElement.clientHeight
      );

      const clientHeight = node.documentElement.clientHeight;

      return scrollHeight - clientHeight;
    } else {
      return node.scrollHeight - node.clientHeight;
    }
  }

  public setNewPageProperties(): void {
    this.scrolled10PercentEventDispatched = false;
    this.scrolled25PercentEventDispatched = false;
    this.scrolled50PercentEventDispatched = false;
    this.scrolled75PercentEventDispatched = false;
    this.scrolled90PercentEventDispatched = false;

    this.navigationToPageDate = Date.now();
    this.pageOnTop = false;
  }

  public dispatchGa4GgScrollEvent(node: Target, currentScroll: number) {
    if (this.getScroll(node, currentScroll) < 10) {
      this.pageOnTop = true;
    }

    if (!this.scrolled10PercentEventDispatched && this.getScroll(node, currentScroll) >= 10 && this.pageOnTop) {
      this.eventService.dispatch<Ga4GgScrollEvent>(
        {
          event: 'scroll',
          scroll_percent: '10%',
        },
        Ga4GgScrollEvent
      );

      this.scrolled10PercentEventDispatched = true;
    }

    if (!this.scrolled25PercentEventDispatched && this.getScroll(node, currentScroll) >= 25 && this.pageOnTop) {
      this.eventService.dispatch<Ga4GgScrollEvent>(
        {
          event: 'scroll',
          scroll_percent: '25%',
        },
        Ga4GgScrollEvent
      );

      this.scrolled25PercentEventDispatched = true;
    }

    if (!this.scrolled50PercentEventDispatched && this.getScroll(node, currentScroll) >= 50 && this.pageOnTop) {
      this.eventService.dispatch<Ga4GgScrollEvent>(
        {
          event: 'scroll',
          scroll_percent: '50%',
        },
        Ga4GgScrollEvent
      );

      this.scrolled50PercentEventDispatched = true;
    }

    if (!this.scrolled75PercentEventDispatched && this.getScroll(node, currentScroll) >= 75 && this.pageOnTop) {
      this.eventService.dispatch<Ga4GgScrollEvent>(
        {
          event: 'scroll',
          scroll_percent: '75%',
        },
        Ga4GgScrollEvent
      );

      this.eventService.dispatch<Ga4GgReaderTypeEvent>(
        {
          event: 'reader_type',
          page_type: this.pageType,
          reader_type: Date.now() - this.navigationToPageDate > 20000 ? 'Reader' : 'Scanner',
          visitor_type: this.authStatePersistenceService.isUserLoggedIn() ? 'logged_in' : 'logged-out',
        },
        Ga4GgReaderTypeEvent
      );

      this.scrolled75PercentEventDispatched = true;
    }

    if (!this.scrolled90PercentEventDispatched && this.getScroll(node, currentScroll) >= 90 && this.pageOnTop) {
      this.eventService.dispatch<Ga4GgScrollEvent>(
        {
          event: 'scroll',
          scroll_percent: '90%',
        },
        Ga4GgScrollEvent
      );

      this.scrolled90PercentEventDispatched = true;
    }
  }
}
